import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { HEADQUARTERS_NAME } from '@speeki/dictionary';
import {
    Button,
    ModalComponent,
    ModalProps,
    PaginatedSelectField,
} from '@speeki/global-ui-components';
import { Modal } from '@speeki/global-ui-components';
import { SessionStorageManager } from '@speeki/storage-manager';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { SubsidiarySiteFromUrlDocument } from '@graphql/generated/graphql';

import { AuthService } from '@services/AuthService/AuthService';

import { useQuery, useReactiveVar } from '@apollo/client';
import { subsidiarySiteUuidFromUrlVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSubsidiarySitesSelect } from '@shared/selects/useSubsidiarySitesSelect';
import { requiredSelectV2 } from '@shared/validations';

interface EntityChangeModalProps extends Pick<ModalProps, 'onClose' | 'open'> {}

export const EntityChangeModal = ({
    onClose,
    open,
}: EntityChangeModalProps) => {
    const { t } = useTranslation();

    const subsidiarySiteUuidFromUrl = useReactiveVar(
        subsidiarySiteUuidFromUrlVar,
    );
    const tenantUuid = AuthService.getTenantUuid();

    const { data } = useQuery(SubsidiarySiteFromUrlDocument, {
        fetchPolicy: 'cache-and-network',
        variables: {
            input: {
                subsidiaryUuid: subsidiarySiteUuidFromUrl || null,
                tenantUuid: tenantUuid || '',
            },
            tenantUuid: tenantUuid,
        },
    });
    const currentSubsidiary = data?.subsidiarySite;
    const isHq = currentSubsidiary?.isHeadquarters ?? false;

    const subsidiarySiteSelect = useSubsidiarySitesSelect({
        variables: { input: { withHeadquarters: true } },
    });

    const {
        control,
        formState: { errors, isDirty, isSubmitting },
        handleSubmit,
        reset,
    } = useForm<ChangeSubsidiarySiteFormValues>({
        mode: 'onChange',
        resolver: yupResolver(ChangeSubsidiarySiteSchema),
        values: {
            subsidiarySite: {
                label:
                    (isHq
                        ? currentSubsidiary?.reportingLabel
                        : currentSubsidiary?.name) || '',
                value: currentSubsidiary?.uuid || HEADQUARTERS_NAME,
            },
        },
    });

    const handleCancel = () => {
        reset();
        onClose();
    };

    const handleSubsidiarySiteChange = (
        data: ChangeSubsidiarySiteFormValues,
    ) => {
        const chosenSubsidiary =
            data.subsidiarySite.value === HEADQUARTERS_NAME
                ? undefined
                : data.subsidiarySite.value;

        subsidiarySiteUuidFromUrlVar(chosenSubsidiary);
        SessionStorageManager.setValue(
            'subsidiarySite',
            chosenSubsidiary ?? '',
        );

        onClose();
    };

    const { field: subsidiarySitesField } = useController({
        control,
        name: 'subsidiarySite',
    });

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CHANGE_SUBSIDIARY_SITE')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {t('CHANGE_SUBSIDIARY_SITE_DESCRIPTION')}
                    </Modal.Description>
                </Modal.Header>

                <StandardForm>
                    <PaginatedSelectField
                        {...subsidiarySitesField}
                        {...subsidiarySiteSelect}
                        closeMenuOnSelect
                        error={!!errors.subsidiarySite}
                        label={`${t('SUBSIDIARY')} *`}
                        message={errors.subsidiarySite?.message}
                        placeholder={t('SELECT_SUBSIDIARY')}
                        testId="oJNFiuB"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={!isDirty || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={handleSubmit(handleSubsidiarySiteChange)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ChangeSubsidiarySiteSchema = yup.object({
    subsidiarySite: requiredSelectV2,
});

type ChangeSubsidiarySiteFormValues = yup.InferType<
    typeof ChangeSubsidiarySiteSchema
>;
